/**
 * Was das ganze KIAN-Gespräch bisher gekostet hat — neben der Zeile unter jeder Antwort.
 *
 * Die Summe kommt vom Server aus `llm_metrics` (jeder Aufruf der Tool-Schleife, gemessene Tokens,
 * Kosten über dieselbe Preisquelle wie die LLM-Statistik). Hier wird nichts addiert oder geschätzt.
 */
import { Text, Tooltip, type TextProps } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { KianSessionUsage } from '../../lib/api/domains/assistant';
import { useAssistantChat } from '../../hooks/useAssistantChat';
import { shortModelName } from '../KianUsageLine';

const nf = new Intl.NumberFormat('de-DE');

/** Unter 1 € in Cent — eine Gesprächssumme von 6 ct stünde sonst als „0,06 €" da, 0,4 ct als „0,00 €". */
function formatSessionCost(eur: number): string {
  if (eur < 1) return `${(eur * 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ct`;
  return `${eur.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

/**
 * Lädt die Gesprächssumme neu, sobald eine Antwort fertig ist. Die Telemetrie schreibt ihre Zeile
 * nebenher (nicht vor der Antwort) — deshalb erst kurz nach der letzten fertigen Antwort fragen,
 * sonst fehlte ihr letzter Aufruf in der Summe.
 */
export function useKianSessionUsage(sessionId: string | null | undefined) {
  const answered = useAssistantChat((s) =>
    sessionId && s.currentSession?.id === sessionId
      ? s.currentSession.messages.filter((m) => m.role === 'ASSISTANT' && !m.metadata?.streaming).length
      : 0,
  );
  const [settled] = useDebouncedValue(answered, 1500);
  return useQuery({
    queryKey: ['kian-session-usage', sessionId, settled],
    queryFn: () => api.getSessionUsage(sessionId!).then((r) => r.usage),
    enabled: !!sessionId,
    staleTime: 30_000,
    // Beim Nachladen den alten Stand stehen lassen — aber nie den eines anderen Gesprächs
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === sessionId ? prev : undefined),
  });
}

export function formatKianSessionUsage(usage: KianSessionUsage): string {
  const parts = [`${nf.format(usage.tokensIn + usage.tokensOut)} Tokens`];
  if (usage.costEur != null) parts.push(`${usage.costEstimated ? '~' : ''}${formatSessionCost(usage.costEur)}`);
  return parts.join(' · ');
}

function usageDetails(usage: KianSessionUsage): string {
  const lines = [
    `Ganzes Gespräch: ${nf.format(usage.calls)} ${usage.calls === 1 ? 'KIAN-Aufruf' : 'KIAN-Aufrufe'}`,
    `Tokens: ${nf.format(usage.tokensIn)} rein / ${nf.format(usage.tokensOut)} raus`,
    usage.costEur != null ? `Kosten: ${formatSessionCost(usage.costEur)}` : 'Kosten: unbekannt',
  ];
  if (usage.byModel.length > 1 || usage.byModel[0]?.calls !== usage.calls) {
    lines.push('');
    for (const m of usage.byModel) {
      const cost = m.costEur != null ? ` · ${formatSessionCost(m.costEur)}` : '';
      lines.push(`${shortModelName(m.model)}: ${nf.format(m.calls)}× · ${nf.format(m.tokensIn + m.tokensOut)} Tokens${cost}`);
    }
  } else if (usage.byModel[0]) {
    lines.push(`Modell: ${shortModelName(usage.byModel[0].model)}`);
  }
  lines.push('');
  if (usage.source === 'messages') {
    lines.push('Älteres Gespräch: aus den Nachrichten geschätzt, Aufrufe der Werkzeuge fehlen.');
  } else if (usage.costEstimated) {
    lines.push('Für mindestens ein Modell ist kein Preis hinterlegt — dort gilt die Pauschale.');
  }
  lines.push('Zuhören und Vorlesen im Gesprächsmodus sind nicht enthalten.');
  return lines.join('\n');
}

/** „Gespräch bisher: 142.300 Tokens · 3,41 ct" mit Aufschlüsselung im Tooltip; nichts, solange nichts lief. */
export function KianSessionUsageText({
  sessionId,
  prefix = 'Gespräch bisher: ',
  ...textProps
}: { sessionId: string | null | undefined; prefix?: string } & TextProps) {
  const { data: usage } = useKianSessionUsage(sessionId);
  if (!usage) return null;
  return (
    <Tooltip label={usageDetails(usage)} withArrow multiline w={320} style={{ whiteSpace: 'pre-line' }}>
      <Text component="span" size="xs" c="dimmed" {...textProps}>
        {prefix}
        {formatKianSessionUsage(usage)}
      </Text>
    </Tooltip>
  );
}
